import React, { useEffect, useState, useMemo } from 'react'
import { createProfanityFilter } from '../../utils/profanityFilter'
import PropTypes from 'prop-types'
import { Messages } from './Messages'
import { ChatInput } from './ChatInput'
import { ChatHeader } from './ChatHeader'
import { selectTermsAndConditionsAccepted } from '../../reducers/callSlice'
import qs from 'qs'
import UtmUtils from '../../utils/utmUtils'
import { useDispatch, useSelector } from 'react-redux'
import { ChatContainer } from './style'
import {
  selectAutoJoinChat,
  selectChatCooldown,
  setDisabledByCooldown,
  setShowAskNameDialog,
} from '../../reducers/uiSlice'
import { usePostHogAnalytics } from '@gojiraf/analytics'
import { ActionBar } from '../Call/ActionBar'
import { Row } from '../Call/Layout.styles'
import { ChatLoader } from './ChatLoader'
import { ChatAccordion } from './ChatAccordion'
import { DefaultBuyerName, useAuth } from '@gojiraf/auth'
import { selectCurrentStore } from '../../reducers/storeSlice'
import { useUtm } from '@gojiraf/useutm'
import { useAmplifyChat, sendMessage, getNextPage } from '@gojiraf/chat'
import { badWords } from '../../constants/badWords'
import { useModeratorUsername } from '../../hooks/useModeratorUsername'
import { useEventById } from '../../hooks/use-event-by-id'

export const ChatModule = ({ isDesktop = false, chatOpen, setChatOpen }) => {
  const { data: event } = useEventById()
  const { user } = useAuth()
  const [chatConnected, setChatConnected] = useState(false)
  const [loadingChatConnection, setLoadingChatConnection] = useState(false)
  const { state: chatState, dispatch: chatDispatch } = useAmplifyChat()
  const [isFetchingMoreMessages, setIsFetchingMoreMessages] = useState(false)
  const queryParams = qs.parse(window.location.search, { ignoreQueryPrefix: true })
  const { utm_medium } = UtmUtils.getUtmObject(queryParams)
  const { showUIComponents } = useUtm(utm_medium)
  const { postHogTrackEvent } = usePostHogAnalytics()
  const dispatch = useDispatch()
  const chatCooldown = useSelector(selectChatCooldown)
  const autoJoinChat = useSelector(selectAutoJoinChat)
  const { getModeratorColorByUsername } = useModeratorUsername()
  const isUserMuted = chatState.isUserMuted
  const {
    storeConfigurations: {
      features: {
        chat: { allowCancel, askNameOnCallStart, showMessageField },
      },
    },
  } = useSelector(selectCurrentStore)
  const termsAndConditionsAccepted = useSelector(selectTermsAndConditionsAccepted)
  const filter = useMemo(() => createProfanityFilter(badWords), [])

  const handleClick = async () => {
    postHogTrackEvent('chat-click-button-enabled')

    initChat()
  }

  useEffect(() => {
    const askNameForChat = () => {
      if (
        user.name === DefaultBuyerName &&
        !isUserMuted &&
        askNameOnCallStart &&
        showUIComponents
      ) {
        dispatch(
          setShowAskNameDialog({
            open: true,
            allowCancel,
          }),
        )
      }
    }

    if (termsAndConditionsAccepted && chatConnected) askNameForChat()
  }, [termsAndConditionsAccepted, chatConnected])

  const toggleChatMobile = (toggle) => {
    setChatOpen(toggle)
    chatDispatch({
      type: 'resetNewMessagesCount',
    })
  }

  async function initChat() {
    if (event === undefined) return
    setLoadingChatConnection(true)

    const { eventId } = event
    setLoadingChatConnection(false)

    if (!eventId) return

    chatDispatch({
      type: 'setEvent',
      payload: {
        eventId,
      },
    })

    chatDispatch({
      type: 'connect',
    })

    setChatConnected(true)
  }

  async function handleSendMessage({ message }) {
    if (filter.isProfane(message.toLowerCase())) {
      console.warn('Message contains profanity and will not be sent')
      return
    }

    if (chatCooldown > 0) {
      dispatch(setDisabledByCooldown(true))
      setTimeout(() => {
        dispatch(setDisabledByCooldown(false))
      }, chatCooldown * 1000)
    }

    const { messageSent } = await sendMessage(chatDispatch, chatState, {
      chatId: chatState.eventId,
      text: message,
      role: 'BUYER',
      username: user.name,
      replyId: null,
      userId: user.id,
    })
    if (messageSent) postHogTrackEvent('chat-message-sent')
  }

  async function fetchMoreMessages() {
    setIsFetchingMoreMessages(true)
    await getNextPage(chatDispatch, chatState.eventId, chatState.nextToken)
    setIsFetchingMoreMessages(false)
  }

  useEffect(() => {
    if (autoJoinChat === true && chatConnected === false) {
      initChat()
    }
  }, [autoJoinChat, chatConnected])

  return (
    <ChatContainer
      isDesktop={isDesktop}
      isLoading={!chatConnected}
      chatOpen={chatOpen}
      data-test="chat-container"
    >
      {isDesktop === true && <ChatHeader />}
      {!chatConnected ? (
        <ChatLoader
          handleClick={handleClick}
          loading={loadingChatConnection}
          isDesktop={isDesktop}
        />
      ) : (
        <>
          <Messages
            isDesktop={isDesktop}
            hasMoreMessages={chatState.nextToken !== null}
            fetchMoreMessages={fetchMoreMessages}
            isFetchingMoreMessages={isFetchingMoreMessages}
            toggleChatMobile={toggleChatMobile}
            user={user}
            getModeratorColorByUsername={getModeratorColorByUsername}
            messages={chatState.messages}
            chatOpen={chatOpen}
            pinnedMessage={chatState.pin}
          >
            {!isDesktop && <ChatAccordion chatOpen={chatOpen} handleClick={() => {}} />}
          </Messages>
          {showMessageField && (
            <Row
              style={{ gap: '0.312rem', padding: isDesktop ? '0' : '0 1rem', alignItems: 'center' }}
            >
              <ChatInput isDesktop={isDesktop} sendMessage={handleSendMessage} />
              {!isDesktop && <ActionBar />}
            </Row>
          )}
        </>
      )}
    </ChatContainer>
  )
}

ChatModule.propTypes = {
  isDesktop: PropTypes.bool,
  chatOpen: PropTypes.bool,
  setChatOpen: PropTypes.func,
  user: PropTypes.object.isRequired,
}
